<template>
  <b-row class="match-height stat-tiles-row">
    <b-col
      v-for="tile in tiles"
      :key="tile.key"
      cols="6"
      sm="3"
    >
      <b-card
        v-b-tooltip.hover.top="tile.breakdown"
        class="stat-tile-card mb-0"
        :class="{ 'stat-tile-card--alert': tile.alert, 'stat-tile-card--clickable': tile.to }"
        :style="{ '--tile-alert-rgb': tile.accentRgb }"
        body-class="p-1"
        role="button"
        :tabindex="tile.to ? 0 : null"
        @click="goToTile(tile)"
        @keyup.enter="goToTile(tile)"
      >
        <div
          v-if="!loading"
          class="stat-tile-card__blob"
          :style="{ background: tile.accentSoft }"
          aria-hidden="true"
        />

        <div
          v-if="!loading"
          class="stat-tile-card__corner-icon"
          :style="{ color: tile.accent }"
          aria-hidden="true"
        >
          <feather-icon
            :icon="tile.icon"
            size="22"
            class="stat-tile-card__blob-icon"
          />
        </div>

        <div class="stat-tile-card__content">
          <div class="d-flex align-items-center mb-50">
            <span
              class="stat-tile-card__icon-badge mr-50"
              :style="{ background: tile.accentSoft, color: tile.accent }"
            >
              <feather-icon
                :icon="tile.icon"
                size="15"
              />
            </span>
            <small class="text-muted">{{ tile.label }}</small>
          </div>

          <div class="stat-tile-card__value-block">
            <b-spinner
              v-if="loading"
              small
              variant="primary"
            />
            <h3
              v-else
              class="font-weight-bolder mb-25"
            >
              {{ tile.value }}
            </h3>

            <small
              v-if="!loading"
              :class="tile.subtitleClass || 'text-muted'"
            >
              {{ tile.subtitle }}
            </small>

            <div
              v-if="!loading && tile.badges"
              class="stat-tile-card__badges mt-50"
            >
              <b-badge
                v-for="badge in tile.badges"
                :key="badge.label"
                :variant="badge.variant"
                pill
                class="mr-50"
              >
                {{ badge.label }}: {{ badge.value }}
              </b-badge>
            </div>
          </div>
        </div>

        <feather-icon
          v-if="tile.to"
          icon="ChevronRightIcon"
          size="14"
          class="stat-tile-card__chevron"
        />
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import {
  BRow, BCol, BCard, BSpinner, BBadge, VBTooltip,
} from 'bootstrap-vue'
import moment from 'moment'
import axios from '@axios'
import store from '@/store'
import { getUserData } from '@/auth/utils'
import enquiryStoreModule from '@/views/enquiry/enquiryStoreModule'
import closedealStoreModule from '@/views/close-deal/closedealStoreModule'

const ENQUIRY_STORE_MODULE_NAME = 'app-enquiry'
const CLOSE_DEAL_STORE_MODULE_NAME = 'app-close-deal'
const ACTIVE_STATUS_ID = 1
// Matches the actionOptions groupings in FilterPanelV2.vue (property listings can be
// dual-purpose, hence the shared id "2" appearing in both groups).
const ACTION_ID_SALE = '2,3'
const ACTION_ID_RENT = '2,4'
// Enquiry status titles (lowercased) that still need sales follow-up, i.e. not yet closed.
const FOLLOW_UP_STATUS_TITLES = ['follow up', 'under offer & nego']

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BSpinner,
    BBadge,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      loading: true,
      activeListings: 0,
      activeForSale: 0,
      activeForRent: 0,
      expiringCount: 0,
      expireStartDate: '',
      expireEndDate: '',
      leaseExpiringCount: 0,
      leaseExpireStartDate: '',
      leaseExpireEndDate: '',
      followUpCount: 0,
      leadsTotal: 0,
      leadsToday: 0,
    }
  },
  computed: {
    isSuperAdmin() {
      const userData = getUserData()
      return !!(userData && userData.role === 'Super Admin')
    },
    // Super Admin sees the lease card scoped to every agent's deals; Sale sees
    // it scoped to just their own (see fetchLeaseExpiring's created_by).
    showLeaseTile() {
      const userData = getUserData()
      return this.isSuperAdmin || !!(userData && userData.role === 'Sale')
    },
    // Sale is lease-focused and doesn't get the property-expiry card;
    // every other role (Admin, Sale Manager, Super Admin) does.
    showPropertyTile() {
      const userData = getUserData()
      return !(userData && userData.role === 'Sale')
    },
    tiles() {
      const tiles = [
        {
          key: 'active',
          label: 'Active Listings',
          value: this.activeListings.toLocaleString('en-US'),
          subtitle: 'Currently on market',
          breakdown: `For Sale: ${this.activeForSale.toLocaleString('en-US')} · For Rent: ${this.activeForRent.toLocaleString('en-US')}`,
          badges: [
            { label: 'Sale', value: this.activeForSale.toLocaleString('en-US'), variant: 'light-success' },
            { label: 'Rent', value: this.activeForRent.toLocaleString('en-US'), variant: 'light-info' },
          ],
          icon: 'HomeIcon',
          accent: '#7367f0',
          accentSoft: '#ece9fd',
          alert: false,
          to: { name: 'property-lists', query: { statusId: ACTIVE_STATUS_ID } },
        },
        {
          key: 'expiring',
          label: 'Property Expiring (2 Mo.)',
          value: this.expiringCount.toLocaleString('en-US'),
          subtitle: 'Listing needs renewal',
          subtitleClass: this.expiringCount > 0 ? 'text-danger' : 'text-muted',
          icon: 'ClockIcon',
          accent: '#ea5455',
          accentSoft: '#fce6e6',
          accentRgb: '234, 84, 85',
          alert: this.expiringCount > 0,
          to: {
            name: 'property-lists',
            query: {
              expire_start: this.expireStartDate,
              expire_end: this.expireEndDate,
            },
          },
        },
        // {
        //   key: 'followup',
        //   label: 'Needs Follow-up',
        //   value: this.followUpCount.toLocaleString('en-US'),
        //   subtitle: 'Enquiries not yet closed',
        //   subtitleClass: this.followUpCount > 0 ? 'text-warning' : 'text-muted',
        //   icon: 'PhoneCallIcon',
        //   iconVariant: this.followUpCount > 0 ? 'light-warning' : 'light-secondary',
        //   alert: false,
        //   to: { name: 'enquiry-lists' },
        // },
        {
          key: 'leads',
          label: 'New Leads',
          value: this.leadsTotal.toLocaleString('en-US'),
          subtitle: this.leadsToday > 0 ? `+${this.leadsToday} today` : 'No new leads today',
          subtitleClass: this.leadsToday > 0 ? 'text-success' : 'text-muted',
          icon: 'UserPlusIcon',
          accent: '#00cfe8',
          accentSoft: '#e0f9fc',
          alert: false,
          to: { name: 'enquiry-lists', query: { isPublic: 1 } },
        },
      ].filter(tile => tile.key !== 'expiring' || this.showPropertyTile)

      if (this.showLeaseTile) {
        tiles.splice(1, 0, {
          key: 'lease',
          label: 'Lease Expiring (2 Mo.)',
          value: this.leaseExpiringCount.toLocaleString('en-US'),
          subtitle: 'Tenant contract needs renewal',
          subtitleClass: this.leaseExpiringCount > 0 ? 'text-warning' : 'text-muted',
          icon: 'FileTextIcon',
          accent: '#ff9f43',
          accentSoft: '#fff1e2',
          accentRgb: '255, 159, 67',
          alert: this.leaseExpiringCount > 0,
          to: {
            name: 'close-deal-lists',
            query: {
              action: 'Rent',
              expireStartDate: this.leaseExpireStartDate,
              expireEndDate: this.leaseExpireEndDate,
            },
          },
        })
      }

      return tiles
    },
  },
  created() {
    if (!store.hasModule(ENQUIRY_STORE_MODULE_NAME)) store.registerModule(ENQUIRY_STORE_MODULE_NAME, enquiryStoreModule)

    const fetches = [
      this.fetchActiveListings(),
      this.fetchFollowUpEnquiries(),
      this.fetchLeads(),
    ]

    if (this.showPropertyTile) fetches.push(this.fetchExpiringSoon())

    if (this.showLeaseTile) {
      if (!store.hasModule(CLOSE_DEAL_STORE_MODULE_NAME)) store.registerModule(CLOSE_DEAL_STORE_MODULE_NAME, closedealStoreModule)
      fetches.push(this.fetchLeaseExpiring())
    }

    Promise.all(fetches).finally(() => {
      this.loading = false
    })
  },
  beforeDestroy() {
    if (store.hasModule(ENQUIRY_STORE_MODULE_NAME)) store.unregisterModule(ENQUIRY_STORE_MODULE_NAME)
    if (store.hasModule(CLOSE_DEAL_STORE_MODULE_NAME)) store.unregisterModule(CLOSE_DEAL_STORE_MODULE_NAME)
  },
  methods: {
    fetchActiveListings() {
      const fetchCount = actionId => axios.post('/property/v2/filter', {
        statusId: ACTIVE_STATUS_ID,
        is_approve: 1,
        action_id: actionId,
        perpage: 1,
        offset: 0,
      }).then(response => response.data.total || 0)

      return Promise.all([
        fetchCount('').then(total => { this.activeListings = total }),
        fetchCount(ACTION_ID_SALE).then(total => { this.activeForSale = total }),
        fetchCount(ACTION_ID_RENT).then(total => { this.activeForRent = total }),
      ]).catch(() => {})
    },
    fetchExpiringSoon() {
      this.expireStartDate = moment().startOf('day').format('YYYY-MM-DD')
      this.expireEndDate = moment().add(2, 'months').endOf('day').format('YYYY-MM-DD')

      return axios.post('/property/v2/filter', {
        expire_start: this.expireStartDate,
        expire_end: this.expireEndDate,
        perpage: 1,
        offset: 0,
      }).then(response => {
        this.expiringCount = response.data.total || 0
      }).catch(() => {})
    },
    fetchLeaseExpiring() {
      const userData = getUserData()
      this.leaseExpireStartDate = moment().startOf('day').format('YYYY-MM-DD')
      this.leaseExpireEndDate = moment().add(2, 'months').endOf('day').format('YYYY-MM-DD')

      // The backend gates /close-deal/v1/lists on the requester's own type_id
      // (closedeal.controller.js), so it must always be sent regardless of role.
      // Super Admin sees every agent's deals; Sale is scoped to their own.
      const createdBy = this.isSuperAdmin ? '' : (userData ? userData.id : '')

      return store.dispatch('app-close-deal/fetchData', {
        perpage: 1,
        offset: 0,
        code: '',
        type_id: userData ? userData.type_id : '',
        created_by: createdBy,
        action: 'Rent',
        expireStartDate: this.leaseExpireStartDate,
        expireEndDate: this.leaseExpireEndDate,
        month: '',
        year: '',
      }).then(response => {
        this.leaseExpiringCount = response.data.total || 0
      }).catch(() => {})
    },
    fetchFollowUpEnquiries() {
      const userData = getUserData()

      // Matches the permission check in useEnquiryList.js so this count agrees
      // with what the user actually sees on the enquiry list.
      const permission = userData && userData.ability
        ? userData.ability.findIndex(s => s.action === 'manage' && s.subject === 'all')
        : -1
      const createdBy = permission < 0 && userData ? userData.id : ''

      const fetchCount = statusId => store.dispatch('app-enquiry/fetchData', {
        perpage: 1,
        offset: 0,
        code: null,
        owner: null,
        place_name: null,
        zone_name: null,
        bedroom: null,
        keyword: '',
        priceMin: null,
        priceMax: null,
        qId: null,
        sortFav: null,
        isPublic: null,
        created_by: createdBy,
        filterLook: '',
        status_id: statusId,
        minDateFilter: null,
        maxDateFilter: null,
      }).then(response => response.data.total || 0)

      return store.dispatch('app-enquiry/fetchgetStatuses', { order_by: 'ordering_count ASC' })
        .then(response => {
          const statuses = (response.data.data || [])
          const followUpStatusIds = statuses
            .filter(s => FOLLOW_UP_STATUS_TITLES.includes(s.title.trim().toLowerCase()))
            .map(s => s.id)

          return Promise.all(followUpStatusIds.map(fetchCount))
        })
        .then(counts => {
          this.followUpCount = counts.reduce((sum, count) => sum + count, 0)
        })
        .catch(() => {})
    },
    fetchLeads() {
      const today = moment().format('YYYY-MM-DD')

      const fetchCount = params => store.dispatch('app-enquiry/fetchData', {
        perpage: 1,
        offset: 0,
        code: null,
        owner: null,
        place_name: null,
        zone_name: null,
        bedroom: null,
        keyword: '',
        priceMin: null,
        priceMax: null,
        qId: null,
        sortFav: null,
        isPublic: 1,
        created_by: '',
        filterLook: '',
        status_id: '',
        minDateFilter: null,
        maxDateFilter: null,
        ...params,
      }).then(response => response.data.total || 0)

      return Promise.all([
        fetchCount({}).then(total => { this.leadsTotal = total }),
        fetchCount({ minDateFilter: today, maxDateFilter: today }).then(total => { this.leadsToday = total }),
      ]).catch(() => {})
    },
    goToTile(tile) {
      if (!tile.to) return
      this.$router.push(tile.to).catch(() => {})
    },
  },
}
</script>

<style scoped>
.stat-tile-card {
  position: relative;
  overflow: hidden;
  transition: box-shadow 0.2s, transform 0.2s, border-color 0.2s;
  border: 1px solid transparent;
}

.stat-tile-card__content {
  position: relative;
  z-index: 1;
  height: 100%;
  padding-right: 34%;
  display: flex;
  flex-direction: column;
}

.stat-tile-card__value-block {
  margin-top: auto;
}

.stat-tile-card--clickable {
  cursor: pointer;
  border-color: rgba(115, 103, 240, 0.15);
}

.stat-tile-card--clickable:hover {
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.12);
  border-color: rgba(115, 103, 240, 0.4);
  transform: translateY(-2px);
}

.stat-tile-card--clickable:focus-visible {
  outline: 2px solid #7367f0;
  outline-offset: 2px;
}

.stat-tile-card__badges .badge {
  font-size: 0.7rem;
  font-weight: 500;
}

.stat-tile-card__blob {
  position: absolute;
  top: -20%;
  right: -20%;
  z-index: 0;
  width: 52%;
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  box-sizing: border-box;
  pointer-events: none;
  transition: transform 0.25s;
}

.stat-tile-card__corner-icon {
  position: absolute;
  top: 0.9rem;
  right: 0.9rem;
  z-index: 1;
  width: 34px;
  height: 34px;
  display: none;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  pointer-events: none;
}

.stat-tile-card__corner-icon::after {
  content: '';
  position: absolute;
  inset: -7px;
  border-radius: 50%;
  border: 1.5px solid currentColor;
  opacity: 0.22;
}

.stat-tile-card__blob-icon {
  position: relative;
  z-index: 1;
}

.stat-tile-card__icon-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  flex-shrink: 0;
}

.stat-tile-card--clickable:hover .stat-tile-card__blob {
  transform: scale(1.08);
}

@media (max-width: 1024px) {
  .stat-tile-card {
    min-height: 172px;
  }

  .stat-tiles-row {
    row-gap: 1rem;
  }

  .stat-tile-card__blob {
    display: none;
  }

  .stat-tile-card__corner-icon {
    display: flex;
    top: 0.85rem;
    right: 0.85rem;
    width: 22px;
    height: 22px;
  }

  .stat-tile-card__corner-icon::after {
    display: none;
  }

  .stat-tile-card__blob-icon {
    width: 16px;
    height: 16px;
  }

  .stat-tile-card__icon-badge {
    display: none;
  }

  .stat-tile-card__content {
    padding-right: 28%;
  }
}

.stat-tile-card__chevron {
  position: absolute;
  right: 0.9rem;
  bottom: 0.9rem;
  color: #b9b9c3;
  transition: transform 0.2s, color 0.2s;
}

.stat-tile-card--clickable:hover .stat-tile-card__chevron {
  color: #7367f0;
  transform: translateX(3px);
}

.stat-tile-card--alert {
  background-color: rgba(var(--tile-alert-rgb, 255, 159, 67), 0.22);
  box-shadow: none;
}

.stat-tile-card--alert.stat-tile-card--clickable {
  border-color: rgba(var(--tile-alert-rgb, 255, 159, 67), 0.35);
}

.stat-tile-card--alert.stat-tile-card--clickable:hover {
  border-color: rgba(var(--tile-alert-rgb, 255, 159, 67), 0.6);
}

.stat-tile-card--alert .stat-tile-card__chevron {
  color: rgba(var(--tile-alert-rgb, 255, 159, 67), 0.7);
}

.stat-tile-card--alert.stat-tile-card--clickable:hover .stat-tile-card__chevron {
  color: rgb(var(--tile-alert-rgb, 255, 159, 67));
}

/* Bootstrap's 12-col grid only divides evenly for certain tile counts
   (col-sm-3 = 4 per row). Since tiles can be toggled on/off, let flexbox
   share the row equally between however many tiles are actually rendered
   instead of pinning each column to a fixed 25% and stranding a gap.
   Switching over at the `sm` tier (576px, not `md`'s 768px) gives tablets
   like iPad Mini headroom in case their reported width lands a little
   under the nominal breakpoint. */
@media (min-width: 576px) {
  .stat-tiles-row .col-sm-3 {
    flex: 1 1 0;
    max-width: 100%;
  }
}
</style>
